<template>
  <div>
    <input
      type="file"
      accept="image/*,application/pdf"
      @change="handleFileChanged"
      ref="fileInputRef"
      hidden
    />

    <div class="__header-container">
      <h4 class="__header-text">Dokumen Pendukung</h4>
    </div>

    <b-card class="mt-3" v-if="isCreatingContract">
      <div class="empty-table-container">
        <img src="@/assets/images/no-data-illustration.png" alt />
        <div>
          <strong>Dokumen Pendukung belum bisa ditambah</strong>
          <p>Simpan <strong>Kontrak</strong> terlebih dahulu</p>
        </div>
      </div>
    </b-card>

    <b-card class="mt-3" v-else>
      <div class="__attachments-container">
        <div v-if="isLoading" class="__loading-overlay" />
        <div v-for="attachment in attachments" :key="attachment.id">
          <div class="__attachment">
            <img v-if="attachment.name.endsWith('pdf')" src="../assets/pdf-icon.png" alt />

            <img v-else :src="attachment.url" alt />

            <button @click="handleDeleteAttachment(attachment.id)" v-if="canEditFields">
              <i class="icon-fill_close"></i>
            </button>
          </div>

          <a :href="attachment.url" target="_blank" class="__attachment-name">
            {{ attachment.name }}
          </a>
        </div>

        <button v-if="canUpload" class="__upload-button" @click="handleTriggerUpload">
          <i class="icon-fill_upload"></i>
        </button>
      </div>

      <div class="__required">
        <span class="__asterisk">*</span>
        <span class="__text">Wajib diisi</span>
      </div>
    </b-card>
  </div>
</template>

<script>
import * as Attachment from '@/services/rest-api/lib/contract/attachment'

export default {
  props: {
    isCreatingContract: {
      type: Boolean,
      default: false,
    },
    canEditFields: {
      type: Boolean,
      default: false,
    },
    vendorId: {
      type: String,
      default: null,
    },
    contractId: {
      type: String,
      default: null,
    },
    versionId: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      attachments: [],
      attachmentLimit: 5,

      isLoading: true,
    }
  },
  computed: {
    canUpload() {
      return this.canEditFields && this.attachments.length < this.attachmentLimit
    },
  },
  watch: {
    '$route.params': 'getData',
    '$route.query': 'getData',
  },
  async mounted() {
    if (this.isCreatingContract) {
      this.isLoading = false
      return
    }

    await this.getData()
  },
  methods: {
    async getData() {
      const res = await Attachment.get({
        vendorId: this.vendorId,
        contractId: this.contractId,
        versionId: this.versionId,
      })

      if (!res || res.errors) {
        this.$mcToast.error('Gagal memuat data')
        this.isLoading = false
        return
      }

      if (res.status == 404 || res.data == null) {
        res.data = []
      }

      this.attachments = res.data
      this.isLoading = false
    },
    async handleFileChanged(e) {
      const MAX_FILE_SIZE = 1024 * 1024 * 2 // 2MB
      const [file] = e.target.files
      if (!file) return

      if (file.size > MAX_FILE_SIZE) {
        this.$mcToast.error({
          component: 'McToastDynamicBody',
          props: {
            contents: [
              {
                html: 'Gagal unggah file. Ukuran melebihi batas ketentuan <strong>(2 MB)</strong>',
              },
            ],
          },
        })

        e.target.value = ''
        return
      }

      this.isLoading = true

      let type = null
      if (file.type.startsWith('image/')) type = 'IMAGE'
      else if (file.type == 'application/pdf') type = 'PDF'
      else {
        this.$mcToast.error({
          component: 'McToastDynamicBody',
          props: {
            contents: [
              {
                html: 'Gagal unggah file. Format tidak didukung (hanya <strong>PDF</strong> dan <strong>Image</strong>)',
              },
            ],
          },
        })

        e.target.value = ''
        this.isLoading = false
        return
      }

      const res = await Attachment.store(
        {
          vendorId: this.vendorId,
          contractId: this.contractId,
          versionId: this.versionId,
        },
        {
          type,
          file,
        }
      )

      if (res.errors) {
        this.$mcToast.error('Gagal mengunggah file')
        e.target.value = ''
        this.isLoading = false
        return
      }

      this.$mcToast.success('Sukses mengunggah file')

      e.target.value = ''

      await this.getData()
      this.isLoading = false
    },

    async handleDeleteAttachment(id) {
      this.isLoading = true

      const res = await Attachment.deleteSingle(
        {
          vendorId: this.vendorId,
          contractId: this.contractId,
          versionId: this.versionId,
        },
        id
      )

      if (res.errors) {
        this.$mcToast.error('Gagal menghapus file')
        this.isLoading = false
        return
      }

      await this.getData()
      this.isLoading = false
    },

    // Utils
    handleTriggerUpload() {
      this.$refs.fileInputRef.click()
    },
  },
}
</script>

<style lang="scss" scoped>
.__attachments-container {
  display: flex;
  gap: 8px;

  .__attachment {
    width: 80px;
    height: 80px;
    overflow: hidden;
    border-radius: 4px;
    position: relative;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    button {
      position: absolute;
      z-index: 1;
      top: 0;
      right: 0;

      border: none;
      border-radius: 100%;
      padding: 0;
      margin: 0;

      cursor: pointer;

      background: red;
      height: 15px;
      width: 15px;

      &:hover {
        transform: scale(0.9);
      }

      i::before {
        font-size: 1em;
        position: absolute;
        color: white;
        transform: translate(-50%, -50%);
        top: 50%;
        left: 50%;
      }
    }
  }

  .__attachment-name {
    margin-top: 10px;

    font-size: 0.8em;
    font-weight: 600;
    text-align: center;

    width: 80px;
    overflow: hidden;

    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;

    color: $primary;
  }
}

.__upload-button {
  background: transparent;
  height: 80px;
  width: 80px;
  border: 2px dashed #c4c4c4;
  border-radius: 4px;
  display: flex;
  justify-content: center;
  align-items: center;

  i::before {
    font-size: 2em;
    color: $teal-200;
  }
}

.__required {
  font-style: italic;
  margin-top: 30px;

  .__asterisk {
    color: $red-500;
  }

  .__text {
    color: $teal-300;
  }
}

.__loading-overlay {
  height: 100%;
  width: 100%;
  position: absolute;
  inset: 0;
  background: white;
  opacity: 0.5;
  z-index: 2;
}
</style>
